import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTablePageSkeleton } from '../components/common/Skeleton';
import { PageFadeSection } from '../components/common/PageFadeSection';
import OperisLoader from '../components/common/OperisLoader';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { parseUTC } from '../utils/dateUtils';

const EmployeesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMyTeamsModal, setShowMyTeamsModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [myTeamsLoading, setMyTeamsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'

  // Current user's teams state
  const [currentUserTeams, setCurrentUserTeams] = useState([]);

  // Add debounce and local search states
  // const searchInputRef = useRef(null);
  const [searchDebounce, setSearchDebounce] = useState(null);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const searchInputRef = useRef(null);

  // Filter states - updated to match UserManagementPage pattern
  const [filters, setFilters] = useState({
    searchTerm: '',
    hasSapId: '',
    isAzureLinked: '',
    page: 1,
    pageSize: 10
  });

  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false
  });

  // Team states
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [assignedTeams, setAssignedTeams] = useState([]);
  const [teamsToAssign, setTeamsToAssign] = useState([]);
  const [teamsToRemove, setTeamsToRemove] = useState([]);

  // Edit form state
  const [editForm, setEditForm] = useState({
    employeeUUId: '',
    sapEmployeeId: '',
    oid: '',
    name: '',
    email: '',
    userId: ''
  });

  // Check if current user has edit permission (role 4 or 3)
  const canEditEmployees = user && (user.role === 4 || user.role === 3);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [filters]);

  // Load teams and assigned teams when detail modal opens
  useEffect(() => {
    if (showDetailModal && selectedEmployee) {
      loadTeams();
      loadAssignedTeams();
    }
  }, [showDetailModal, selectedEmployee]);

  // Load teams when edit modal opens
  useEffect(() => {
    if (showEditModal && selectedEmployee) {
      loadTeams();
      loadAssignedTeams();
      // Reset team assignment states
      setTeamsToAssign([]);
      setTeamsToRemove([]);
    }
  }, [showEditModal, selectedEmployee]);

  // Load user's teams when "My Teams" modal opens
  useEffect(() => {
    if (showMyTeamsModal) {
      loadCurrentUserTeams();
    }
  }, [showMyTeamsModal]);

  // Add effect to sync localSearchTerm when filters change externally (like reset)
  useEffect(() => {
    setLocalSearchTerm(filters.searchTerm);
  }, [filters.searchTerm]);

  // Add cleanup for debounce
  useEffect(() => {
    return () => {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
    };
  }, [searchDebounce]);

  // Add effect to prevent body scroll when modals are open
  useEffect(() => {
    if (showDetailModal || showEditModal || showMyTeamsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDetailModal, showEditModal, showMyTeamsModal]);

  // Function to load current user's teams
  const loadCurrentUserTeams = async () => {
    try {
      setMyTeamsLoading(true);
      const response = await api.get('/api/teams/my-teams');
      
      if (response.data.isSuccess) {
        setCurrentUserTeams(response.data.data || []);
      } else {
        toast.error(response.data.message || 'Failed to load your teams');
      }
    } catch (err) {
      console.error('Error loading user teams:', err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors?.join(', ') || 
                          err.message || 
                          'Failed to load your teams';
      toast.error(errorMessage);
    } finally {
      setMyTeamsLoading(false);
    }
  };

  const loadTeams = async () => {
    try {
      const res = await api.get("/api/teams");
      if (res.data.isSuccess) {
        setTeams(res.data.data);
      }
    } catch {
      toast.error("Failed to load teams");
    }
  };

  const loadAssignedTeams = async () => {
    if (!selectedEmployee?.userId) return;

    try {
      const res = await api.get(`/api/users/${selectedEmployee.userId}/teams`);
      if (res.data.isSuccess) {
        setAssignedTeams(res.data.data || []);
      }
    } catch {
      // Silent fail - not all users may have teams
    }
  };

  // Load stats from API
  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get("/api/employees/EmployeeStats");
      
      if (response.data.isSuccess) {
        const apiStats = response.data.data;
        setStats({
          totalEmployees: apiStats.totalEmployees || 0,
          withSapId: apiStats.withSapId || 0,
          withoutSapId: apiStats.withoutSapId || 0,
          isAzureLinked: apiStats.azureLinked || 0,
          hasEmployeeUUId: apiStats.withUuid || 0
        });
      } else {
        console.error('Failed to load stats:', response.data.message);
        calculateStatsFromEmployees();
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      calculateStatsFromEmployees();
    } finally {
      setStatsLoading(false);
    }
  };

  // Fallback function to calculate stats from employees data
  const calculateStatsFromEmployees = () => {
    const calculatedStats = {
      totalEmployees: pagination.totalCount || employees.length,
      withSapId: employees.filter(e => e.sapEmployeeId).length,
      withoutSapId: employees.filter(e => !e.sapEmployeeId).length,
      isAzureLinked: employees.filter(e => e.isAzureLinked).length,
      hasEmployeeUUId: employees.filter(e => e.employeeUUId).length
    };
    setStats(calculatedStats);
  };

  // Updated handleFilterChange with debounce for search
  const handleFilterChange = (key, value) => {
    if (key === 'searchTerm') {
      // Update local state immediately for the input
      setLocalSearchTerm(value);
      
      // Clear existing timeout
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
      
      // Set new timeout for API call
      const timeout = setTimeout(() => {
        setFilters(prev => ({
          ...prev,
          searchTerm: value,
          page: 1
        }));
        
        // Restore focus after filter update
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 0);
      }, 500); // Wait 500ms after user stops typing
      
      setSearchDebounce(timeout);
    } else {
      // For other filters, update immediately
      setFilters(prev => ({
        ...prev,
        [key]: value,
        page: 1
      }));
    }
  };

  // Add effect to restore focus when employees update
  useEffect(() => {
    if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
      // Only restore if it was previously focused
      // You might want to track focus state
    }
  }, [employees]);

  const handleResetFilters = () => {
    setFilters({
      searchTerm: '',
      hasSapId: '',
      isAzureLinked: '',
      page: 1,
      pageSize: 10
    });
    setLocalSearchTerm(''); // Reset local search term
    toast.success('Filters reset successfully');
  };

  const handleTeamSelection = (teamId) => {
    setSelectedTeamId(teamId);
  };

  const addTeamToAssign = () => {
    if (!selectedTeamId) {
      toast.error("Please select a team");
      return;
    }

    const team = teams.find(t => t.id === selectedTeamId);
    if (!team) return;

    // Check if already in assigned teams
    const alreadyAssigned = assignedTeams.some(t => t.id === selectedTeamId);
    if (alreadyAssigned) {
      toast.error("This team is already assigned to the employee");
      return;
    }

    // Check if already in teamsToAssign
    const alreadyInQueue = teamsToAssign.some(t => t.id === selectedTeamId);
    if (alreadyInQueue) {
      toast.error("This team is already in the assignment queue");
      return;
    }

    setTeamsToAssign(prev => [...prev, team]);
    setSelectedTeamId("");
    toast.success(`Added ${team.name} to assignment queue`);
  };

  const removeTeamFromAssign = (teamId) => {
    setTeamsToAssign(prev => prev.filter(team => team.id !== teamId));
    toast.success("Team removed from assignment queue");
  };

  const markTeamForRemoval = (teamId) => {
    const team = assignedTeams.find(t => t.id === teamId);
    if (!team) return;

    setTeamsToRemove(prev => [...prev, team]);
    toast.success(`Marked ${team.name} for removal`);
  };

  const unmarkTeamForRemoval = (teamId) => {
    setTeamsToRemove(prev => prev.filter(team => team.id !== teamId));
    toast.success("Team unmarked for removal");
  };

  // Function to open "My Teams" modal
  const openMyTeamsModal = () => {
    setShowMyTeamsModal(true);
  };

  // Function to open edit modal with permission check
  const openEditModal = (employee) => {
    if (!canEditEmployees) {
      toast.error("You don't have permission to edit employees");
      return;
    }

    setSelectedEmployee(employee);
    setEditForm({
      employeeUUId: employee.employeeUUId || '',
      sapEmployeeId: employee.sapEmployeeId || '',
      oid: employee.oid || '',
      name: employee.name || '',
      email: employee.email || '',
      userId: employee.userId || ''
    });
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  // Handle edit form changes
  const handleEditFormChange = (key, value) => {
    if (!canEditEmployees) {
      toast.error("You don't have permission to edit employees");
      return;
    }

    setEditForm(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Perform team assignments
  const performTeamAssignments = async () => {
    if (!canEditEmployees) {
      toast.error("You don't have permission to assign teams");
      return;
    }

    if (!selectedEmployee?.userId) {
      toast.error("Employee must have a User ID to assign teams");
      return;
    }

    const assignmentPromises = [];

    // Assign new teams
    for (const team of teamsToAssign) {
      assignmentPromises.push(
        api.post(`/api/teams/${team.id}/members`, {
          userId: selectedEmployee.userId
        })
      );
    }

    // Remove marked teams
    for (const team of teamsToRemove) {
      assignmentPromises.push(
        api.delete(`/api/teams/${team.id}/members/${selectedEmployee.userId}`)
      );
    }

    try {
      await Promise.all(assignmentPromises);
      if (teamsToAssign.length > 0 || teamsToRemove.length > 0) {
        toast.success(`Team assignments updated successfully`);
      }
    } catch (err) {
      throw new Error(err.response?.data?.message || "Failed to update team assignments");
    }
  };

  // Handle edit submit - combines employee update and team assignment
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!canEditEmployees) {
      toast.error("You don't have permission to edit employees");
      return;
    }
    
    if (!selectedEmployee) return;

    const updatePromise = new Promise(async (resolve, reject) => {
      try {
        setEditLoading(true);

        // Prepare request body according to API specification
        const requestBody = {
          employeeUUId: editForm.employeeUUId.trim(),
          sapEmployeeId: editForm.sapEmployeeId.trim(),
          oid: editForm.oid.trim(),
          name: editForm.name.trim(),
          email: editForm.email.trim(),
          userId: editForm.userId.trim()
        };

        // Update employee details
        const response = await api.put(`/api/employees/${selectedEmployee.id}`, requestBody);

        if (response.data.isSuccess) {
          // Update team assignments if there are any changes
          if (teamsToAssign.length > 0 || teamsToRemove.length > 0) {
            await performTeamAssignments();
          }

          // Update local state for employee
          setEmployees(prevEmployees =>
            prevEmployees.map(employee =>
              employee.id === selectedEmployee.id
                ? { ...employee, ...response.data.data }
                : employee
            )
          );

          // Update selected employee
          setSelectedEmployee(prev => ({ ...prev, ...response.data.data }));

          // Refresh stats from API
          loadStats();

          // Refresh assigned teams
          if (selectedEmployee.userId) {
            loadAssignedTeams();
          }

          // Clear team assignment queues
          setTeamsToAssign([]);
          setTeamsToRemove([]);

          // Close modal
          setShowEditModal(false);

          resolve('Employee and team assignments updated successfully!');
        } else {
          reject(new Error(response.data.message || 'Failed to update employee'));
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to update employee';
        reject(new Error(errorMessage));
        console.error('Error updating employee:', err);
      } finally {
        setEditLoading(false);
      }
    });

    toast.promise(
      updatePromise,
      {
        loading: 'Updating employee and team assignments...',
        success: (message) => message,
        error: (err) => err.message || 'Failed to update',
      },
      {
        style: {
          minWidth: '250px',
        },
        success: {
          duration: 4000,
          icon: '✅',
        },
        error: {
          duration: 5000,
          icon: '❌',
        },
      }
    );
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });

      const response = await api.get(`/api/employees?${params.toString()}`);

      if (response.data.isSuccess) {
        const data = response.data.data;
        let employeesList = [];
        let totalCount = 0;

        if (Array.isArray(data)) {
          employeesList = data;
          totalCount = data.length;
        } else if (data && Array.isArray(data.items)) {
          employeesList = data.items;
          totalCount = data.totalCount || data.items.length;
        } else if (data && data.data && Array.isArray(data.data)) {
          employeesList = data.data;
          totalCount = data.totalCount || data.data.length;
        }

        setEmployees(employeesList);
        setPagination({
          page: data?.page || 1,
          pageSize: data?.pageSize || 20,
          totalCount: totalCount || 0,
          totalPages: data?.totalPages || Math.ceil((totalCount || 0) / (data?.pageSize || 20)),
          hasPreviousPage: data?.hasPreviousPage || false,
          hasNextPage: data?.hasNextPage || false
        });
      } else {
        toast.error(response.data.message || 'Failed to load employees');
        setEmployees([]);
      }

      // Load stats from API
      loadStats();
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to load data';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error loading data:', err);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to get display stats - uses API data if available
  const getDisplayStats = () => {
    if (stats) {
      return {
        totalEmployees: stats.totalEmployees || 0,
        withSapId: stats.withSapId || 0,
        withoutSapId: stats.withoutSapId || 0,
        isAzureLinked: stats.isAzureLinked || 0,
        hasEmployeeUUId: stats.hasEmployeeUUId || 0
      };
    }

    // Fallback to calculated stats if API data is not available yet
    return {
      totalEmployees: pagination.totalCount || 0,
      withSapId: employees.filter(e => e.sapEmployeeId).length,
      withoutSapId: employees.filter(e => !e.sapEmployeeId).length,
      isAzureLinked: employees.filter(e => e.isAzureLinked).length,
      hasEmployeeUUId: employees.filter(e => e.employeeUUId).length
    };
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = parseUTC(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSapStatusColor = (hasSapId) => {
    return hasSapId
      ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
      : 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';
  };

  const getAzureStatusColor = (isAzureLinked) => {
    return isAzureLinked
      ? 'bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800'
      : 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';
  };

  const displayStats = getDisplayStats();

  const renderStatsCards = () => (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      {/* Total Employees - Blue */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide">Total Employees</div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-blue-200 dark:bg-blue-700 animate-pulse rounded"></div>
              ) : (
                displayStats.totalEmployees?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
          All employee records
        </div>
      </div>

      {/* With SAP ID - Green */}
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 rounded-lg p-4 border border-emerald-200 dark:border-emerald-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">With SAP ID</div>
            <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-emerald-200 dark:bg-emerald-700 animate-pulse rounded"></div>
              ) : (
                displayStats.withSapId?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
          SAP integrated
        </div>
      </div>

      {/* Without SAP ID - Yellow */}
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 rounded-lg p-4 border border-amber-200 dark:border-amber-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide">Without SAP ID</div>
            <div className="text-2xl font-bold text-amber-900 dark:text-amber-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-amber-200 dark:bg-amber-700 animate-pulse rounded"></div>
              ) : (
                displayStats.withoutSapId?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          Awaiting SAP sync
        </div>
      </div>

      {/* With UUID - Indigo */}
      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/30 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-400 uppercase tracking-wide">With UUID</div>
            <div className="text-2xl font-bold text-indigo-900 dark:text-indigo-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-indigo-200 dark:bg-indigo-700 animate-pulse rounded"></div>
              ) : (
                displayStats.hasEmployeeUUId?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-indigo-600 dark:text-indigo-400">
          Has employee UUID
        </div>
      </div>

      {/* Azure Linked - Purple */}
      <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-lg p-4 border border-purple-200 dark:border-purple-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-purple-700 dark:text-purple-400 uppercase tracking-wide">Azure Linked</div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-purple-200 dark:bg-purple-700 animate-pulse rounded"></div>
              ) : (
                displayStats.isAzureLinked?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-purple-600 dark:text-purple-400">
          Azure AD connected
        </div>
      </div>
    </div>
  );

  const renderEmployeeCard = (employee) => (
    <div key={employee.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <div className="p-6">
        {/* Employee Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 h-12 w-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-lg">
                {employee.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'EE'}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{employee.name || 'No Name'}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{employee.email}</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSapStatusColor(employee.sapEmployeeId)}`}>
              {employee.sapEmployeeId ? 'SAP Linked' : 'No SAP ID'}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAzureStatusColor(employee.isAzureLinked)}`}>
              {employee.isAzureLinked ? 'Azure Linked' : 'No Azure'}
            </span>
          </div>
        </div>

        {/* Employee Details */}
        <div className="space-y-3 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">SAP Employee ID</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{employee.sapEmployeeId || 'Not assigned'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Employee UUID</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {employee.employeeUUId || 'Not assigned'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">User ID</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {employee.userId || 'Not linked'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Azure Status</p>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${employee.isAzureLinked ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                {employee.isAzureLinked ? 'Linked' : 'Not Linked'}
              </span>
            </div>
          </div>
        </div>

        {/* Employee Timeline */}
        <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-400">Employee ID:</span>
              <span className="font-medium text-gray-900 dark:text-gray-200">{employee.id?.substring(0, 8)}...</span>
            </div>

            {employee.userId && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Linked User ID:</span>
                <span className="font-medium text-green-600 dark:text-green-400 truncate">{employee.userId?.substring(0, 8)}...</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Button - Only show edit button if user has permission */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          {canEditEmployees ? (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedEmployee(employee);
                  setShowDetailModal(true);
                }}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-md hover:from-blue-600 hover:to-indigo-700 text-sm font-medium transition-all duration-200"
              >
                View Details
              </button>
              <button
                onClick={() => openEditModal(employee)}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-md hover:from-green-600 hover:to-emerald-700 text-sm font-medium transition-all duration-200"
              >
                Edit
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setSelectedEmployee(employee);
                setShowDetailModal(true);
              }}
              className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-md hover:from-blue-600 hover:to-indigo-700 text-sm font-medium transition-all duration-200"
            >
              View Details
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Function to render "My Teams" modal
  const renderMyTeamsModal = () => (
    showMyTeamsModal && (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
          onClick={() => setShowMyTeamsModal(false)}
        />

        {/* Modal Container */}
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-indigo-600 to-purple-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-14 w-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                    <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-7.397a6 6 0 00-9 5.197" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">My Teams</h2>
                    <p className="text-indigo-100 text-base">
                      Teams you're a member of
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMyTeamsModal(false)}
                  className="text-white/90 hover:text-white p-2 hover:bg-white/20 rounded-lg transition-all duration-200"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-6">
              {myTeamsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <OperisLoader size="md" message="Loading teams..." />
                </div>
              ) : currentUserTeams.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-16 w-16 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-7.397a6 6 0 00-9 5.197" />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No teams assigned</h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    You are not currently a member of any teams.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentUserTeams.map((team) => (
                      <div key={team.id} className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg p-4 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all duration-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-7.397a6 6 0 00-9 5.197" />
                              </svg>
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white">{team.name}</h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400">Team</p>
                            </div>
                          </div>
                          {/* Show if user is lead - adjust based on your API response */}
                          {team.leadUserId && (
                            <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full">
                              Lead
                            </span>
                          )}
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          {team.description && (
                            <p className="text-gray-600 dark:text-gray-400 line-clamp-2">{team.description}</p>
                          )}
                          
                          {/* Show lead info if exists */}
                          {team.lead && (
                            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span>Lead: {team.lead.name || team.lead.email}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <div className="flex items-center space-x-1">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              <span>Members: {team.members?.length || 0}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Created: {formatDate(team.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        You are a member of <span className="font-semibold">{currentUserTeams.length}</span> team{currentUserTeams.length !== 1 ? 's' : ''}.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 flex justify-end">
              <button
                onClick={() => setShowMyTeamsModal(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  if (loading && !employees.length) {
    return <DataTablePageSkeleton />;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <PageFadeSection>
      {/* Header with Role Indicator */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="relative mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Employees
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  Manage employee records and SAP integration
                </p>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-2 left-0 h-0.5 w-24 bg-gradient-to-r from-green-500 to-transparent"></div>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {employees.length} of {pagination.totalCount} employees
          </div>
          {/* Role Indicator */}
          {user && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Logged in as:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                user.role === 4 
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' 
                  : user.role === 3 
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' 
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {user.role === 4 ? 'Administrator' : user.role === 3 ? 'Manager' : 'User'}
              </span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
          <button
            onClick={() => {
              setError(null);
              toast.success('Error cleared');
            }}
            className="float-right hover:text-red-900"
          >
            &times;
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {renderStatsCards()}
      </PageFadeSection>

      <PageFadeSection>
      {/* Filters - Updated to match UserManagementPage style */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 md:mb-0">Filters</h2>
          <div className="flex gap-2">
            {/* "My Teams" Button */}
            <button
              onClick={openMyTeamsModal}
              className="px-4 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 border border-indigo-300 dark:border-indigo-600 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-7.397a6 6 0 00-9 5.197" />
              </svg>
              My Teams
            </button>
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset Filters
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Search Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, email, or SAP ID..."
              value={localSearchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* SAP Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SAP Status</label>
              <select
                value={filters.hasSapId || ''}
                onChange={(e) => handleFilterChange('hasSapId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All</option>
                <option value="true">Has SAP ID</option>
                <option value="false">No SAP ID</option>
              </select>
            </div>

            {/* Azure Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Azure Status</label>
              <select
                value={filters.isAzureLinked || ''}
                onChange={(e) => handleFilterChange('isAzureLinked', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All</option>
                <option value="true">Azure Linked</option>
                <option value="false">Not Linked</option>
              </select>
            </div>

            {/* Page Size Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Items per page</label>
              <select
                value={filters.pageSize}
                onChange={(e) => handleFilterChange('pageSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Active Filters Display */}
          {Object.values(filters).some(value => value && value !== '' && value !== 20) && (
            <div className="flex flex-wrap gap-2 pt-2">
              {Object.entries(filters).map(([key, value]) => {
                if (value && value !== '' && key !== 'page' && key !== 'pageSize') {

                  // Helper function to get display value
                  const getDisplayValue = (key, value) => {
                    // SAP Status mapping
                    if (key === 'hasSapId') {
                      return value === 'true' ? 'Has SAP ID' : value === 'false' ? 'No SAP ID' : value;
                    }
                    
                    // Azure Status mapping
                    if (key === 'isAzureLinked') {
                      return value === 'true' ? 'Azure Linked' : value === 'false' ? 'Not Linked' : value;
                    }
                    
                    // For other fields (searchTerm), return as is
                    return value;
                  };

                  // Helper function to get display label
                  const getDisplayLabel = (key) => {
                    const labelMap = {
                      searchTerm: 'Search',
                      hasSapId: 'SAP Status',
                      isAzureLinked: 'Azure Status'
                    };
                    return labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                  };

                  return (
                    <span
                      key={key}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                    >
                      {getDisplayLabel(key)}: {getDisplayValue(key, value)}
                      <button
                        onClick={() => handleFilterChange(key, '')}
                        className="ml-1 hover:text-green-600 dark:hover:text-green-300"
                      >
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  );
                }
                return null;
              })}
            </div>
          )}
        </div>
      </div>
      </PageFadeSection>

      <PageFadeSection>
      {/* View Toggle & Employees Grid */}
      {employees.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No employees found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your filters or search term.</p>
          <div className="mt-6">
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
            >
              Reset Filters
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* View Toggle */}
          <div className="flex items-center justify-end mb-4 gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400 mr-1">View:</span>
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'card' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              title="Card View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1" strokeWidth="2"/><rect x="14" y="3" width="7" height="7" rx="1" strokeWidth="2"/><rect x="3" y="14" width="7" height="7" rx="1" strokeWidth="2"/><rect x="14" y="14" width="7" height="7" rx="1" strokeWidth="2"/></svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg transition-all duration-200 ${viewMode === 'table' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              title="Table View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
            </button>
          </div>

          {viewMode === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {employees.map(renderEmployeeCard)}
          </div>
          ) : (
          /* Table View */
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">SAP Employee ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Employee UUID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">SAP Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Azure Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {employees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 h-9 w-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {employee.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'EE'}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">{employee.name || 'No Name'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{employee.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{employee.sapEmployeeId || <span className="text-gray-400 dark:text-gray-500 italic">Not assigned</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200 font-mono truncate max-w-[160px]">{employee.employeeUUId || <span className="text-gray-400 dark:text-gray-500 italic">Not assigned</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSapStatusColor(employee.sapEmployeeId)}`}>
                          {employee.sapEmployeeId ? 'SAP Linked' : 'No SAP ID'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getAzureStatusColor(employee.isAzureLinked)}`}>
                          {employee.isAzureLinked ? 'Linked' : 'Not Linked'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setSelectedEmployee(employee); setShowDetailModal(true); }}
                            className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors duration-200"
                          >
                            View
                          </button>
                          {canEditEmployees && (
                            <button
                              onClick={() => openEditModal(employee)}
                              className="px-3 py-1.5 text-xs font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors duration-200"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-lg shadow">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPreviousPage}
                  className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{((pagination.page - 1) * pagination.pageSize) + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.totalCount}</span> results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.hasPreviousPage}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Previous
                    </button>
                    {[...Array(pagination.totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => handlePageChange(i + 1)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold transition-colors duration-200 ${pagination.page === i + 1
                          ? 'z-10 bg-gradient-to-r from-green-500 to-emerald-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600'
                          : 'text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0'
                          }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.hasNextPage}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </PageFadeSection>

      {/* My Teams Modal */}
      {renderMyTeamsModal()}

      {/* Employee Detail Modal - Hide edit button if no permission */}
      {showDetailModal && selectedEmployee && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
            onClick={() => setShowDetailModal(false)}
          />

          {/* Modal Container */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
              {/* Modal Header */}
              <div className="relative bg-gradient-to-r from-green-600 to-emerald-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <span className="text-white font-bold text-xl">
                        {selectedEmployee.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'EE'}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedEmployee.name || 'No Name'}</h2>
                      <p className="text-green-100 text-base">{selectedEmployee.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-white/90 hover:text-white p-2 hover:bg-white/20 rounded-lg transition-all duration-200"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="p-6 space-y-8">
                  {/* Employee Information */}
                  <div className="space-y-6">
                    <div className="flex items-center space-x-3">
                      <div className="h-6 w-1.5 bg-green-600 rounded-full"></div>
                      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">Employee Information</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">Full Name</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedEmployee.name || 'Not specified'}</p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89-4.26a2 2 0 012.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">Email Address</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedEmployee.email}</p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">SAP Employee ID</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedEmployee.sapEmployeeId || 'Not assigned'}</p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">Employee UUID</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedEmployee.employeeUUId || 'Not assigned'}</p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">OID</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedEmployee.oid || 'Not assigned'}</p>
                      </div>

                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">User ID</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedEmployee.userId || 'Not linked'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer - Hide edit button if no permission */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Employee Record</span>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 transition-all duration-200"
                  >
                    Close
                  </button>
                  {canEditEmployees && (
                    <button
                      onClick={() => openEditModal(selectedEmployee)}
                      className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:from-green-600 hover:to-emerald-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200"
                    >
                      Edit Employee
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal - Only show if user has permission */}
      {showEditModal && selectedEmployee && canEditEmployees && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
            onClick={() => setShowEditModal(false)}
          />

          {/* Modal Container */}
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Modal Content */}
            <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">

              {/* Modal Header */}
              <div className="relative bg-gradient-to-r from-green-600 to-emerald-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <span className="text-white font-bold text-xl">
                        {selectedEmployee.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'EE'}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Edit Employee</h2>
                      <p className="text-green-100 text-base">{selectedEmployee.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-white/90 hover:text-white p-2 hover:bg-white/20 rounded-lg transition-all duration-200"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Edit Form */}
              <form onSubmit={handleEditSubmit} className="p-6">
                <div className="space-y-6">
                  {/* Team Assignment Field */}
                  {selectedEmployee.userId && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Team Assignment
                      </label>

                      {/* Team Selection */}
                      <div className="flex gap-3">
                        <select
                          value={selectedTeamId}
                          onChange={(e) => handleTeamSelection(e.target.value)}
                          className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 dark:text-white"
                        >
                          <option value="">Select a team to add...</option>
                          {teams.map(team => (
                            <option key={team.id} value={team.id}>{team.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={addTeamToAssign}
                          disabled={!selectedTeamId}
                          className="px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          Add to Queue
                        </button>
                      </div>

                      {/* Teams to Assign Queue */}
                      {teamsToAssign.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teams to be assigned:</p>
                          <div className="space-y-2">
                            {teamsToAssign.map(team => (
                              <div key={team.id} className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 px-4 py-3 rounded-lg border border-blue-200 dark:border-blue-700">
                                <div className="flex items-center space-x-3">
                                  <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                    <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-7.397a6 6 0 00-9 5.197" />
                                    </svg>
                                  </div>
                                  <span className="font-medium text-gray-800 dark:text-gray-200">{team.name}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeTeamFromAssign(team.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors duration-200"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Currently Assigned Teams */}
                      {assignedTeams.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Currently assigned teams:</p>
                          <div className="space-y-2">
                            {assignedTeams.map(team => {
                              const markedForRemoval = teamsToRemove.some(t => t.id === team.id);
                              return (
                                <div key={team.id} className={`flex items-center justify-between px-4 py-3 rounded-lg border ${markedForRemoval ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                                  <div className="flex items-center space-x-3">
                                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${markedForRemoval ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-100 dark:bg-gray-600'}`}>
                                      <svg className={`h-4 w-4 ${markedForRemoval ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-7.397a6 6 0 00-9 5.197" />
                                      </svg>
                                    </div>
                                    <span className={`font-medium ${markedForRemoval ? 'text-red-700 dark:text-red-400 line-through' : 'text-gray-800 dark:text-gray-200'}`}>
                                      {team.name}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => markedForRemoval ? unmarkTeamForRemoval(team.id) : markTeamForRemoval(team.id)}
                                    className={`px-3 py-1.5 rounded-lg transition-colors duration-200 ${markedForRemoval ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`}
                                  >
                                    {markedForRemoval ? 'Keep' : 'Remove'}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={editForm.name}
                      onChange={(e) => handleEditFormChange('name', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter full name"
                    />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={editForm.email}
                      onChange={(e) => handleEditFormChange('email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter email address"
                    />
                  </div>

                  {/* SAP Employee ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      SAP Employee ID
                    </label>
                    <input
                      type="text"
                      value={editForm.sapEmployeeId}
                      onChange={(e) => handleEditFormChange('sapEmployeeId', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter SAP Employee ID"
                    />
                  </div>

                  {/* Employee UUID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Employee UUID
                    </label>
                    <input
                      type="text"
                      value={editForm.employeeUUId}
                      onChange={(e) => handleEditFormChange('employeeUUId', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter Employee UUID"
                    />
                  </div>

                  {/* OID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      OID
                    </label>
                    <input
                      type="text"
                      value={editForm.oid}
                      onChange={(e) => handleEditFormChange('oid', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter OID"
                    />
                  </div>
                </div>

                {/* Summary of Changes */}
                {(teamsToAssign.length > 0 || teamsToRemove.length > 0) && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Summary of Team Changes:</h4>
                    <div className="space-y-1">
                      {teamsToAssign.length > 0 && (
                        <p className="text-sm text-green-600">
                          <svg className="inline w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Will assign {teamsToAssign.length} new team(s)
                        </p>
                      )}
                      {teamsToRemove.length > 0 && (
                        <p className="text-sm text-red-600">
                          <svg className="inline w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Will remove {teamsToRemove.length} team(s)
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Modal Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-600 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    disabled={editLoading}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg hover:from-green-600 hover:to-emerald-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Updating...
                      </span>
                    ) : (
                      'Update Employee & Teams'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;